<template>
  <div class="regulatory-alerts-page">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="brand-header">
          <img 
            src="@/assets/accessiq-logo.png" 
            alt="AccessIQ" 
            class="accessiq-logo-img"
          />
          <span class="brand-subtitle">Alerts</span>
        </div>
        <p class="page-description">
          Stay informed about important regulatory changes, upcoming effective dates, and high-impact rules.
        </p>
      </div>
      <div class="header-actions">
        <button 
          v-if="unreadCount > 0"
          @click="markAllRead" 
          class="btn btn-outline-primary"
          :disabled="markingAllRead"
        >
          <i class="fas fa-check-double mr-2"></i>
          Mark All Read
        </button>
        <router-link to="/regulatory-intelligence" class="btn btn-secondary">
          <i class="fas fa-compass mr-2"></i>
          Dashboard
        </router-link>
      </div>
    </div>

    <!-- Stats Bar -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-value">{{ unreadCount }}</span>
        <span class="stat-label">Unread</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">{{ urgentCount }}</span>
        <span class="stat-label">Urgent</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">{{ alerts.length }}</span>
        <span class="stat-label">Total</span>
      </div>
    </div>

    <!-- Filter Tabs -->
    <div class="filter-tabs">
      <button 
        @click="activeFilter = 'all'"
        class="filter-tab"
        :class="{ 'active': activeFilter === 'all' }"
      >
        All
      </button>
      <button 
        @click="activeFilter = 'unread'"
        class="filter-tab"
        :class="{ 'active': activeFilter === 'unread' }"
      >
        Unread
        <span v-if="unreadCount > 0" class="tab-badge">{{ unreadCount }}</span>
      </button>
      <button 
        @click="activeFilter = 'effective_soon'"
        class="filter-tab"
        :class="{ 'active': activeFilter === 'effective_soon' }"
      >
        <i class="fas fa-calendar-alt mr-1"></i>
        Effective Soon
      </button>
      <button 
        @click="activeFilter = 'high_impact'"
        class="filter-tab"
        :class="{ 'active': activeFilter === 'high_impact' }"
      >
        <i class="fas fa-exclamation-triangle mr-1"></i>
        High Impact
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fas fa-bell fa-spin fa-3x"></i>
        <p>Loading alerts...</p>
      </div>
    </div>

    <!-- Alerts Content -->
    <div v-else class="alerts-content">
      <div v-if="filteredAlerts.length === 0" class="empty-state">
        <i class="fas fa-bell-slash fa-4x"></i>
        <h3>No Alerts</h3>
        <p v-if="activeFilter === 'all'">
          You're all caught up! New alerts will appear here when regulatory changes match your criteria.
        </p>
        <p v-else>
          No {{ activeFilter === 'unread' ? 'unread' : activeFilter.replace('_', ' ') }} alerts.
        </p>
      </div>

      <div v-else class="alerts-list">
        <div 
          v-for="alert in filteredAlerts" 
          :key="alert.id" 
          class="alert-card"
          :class="{ 
            'unread': !alert.is_read,
            'urgent': alert.priority === 'urgent',
            'high': alert.priority === 'high'
          }"
        >
          <div class="alert-priority-bar" :class="alert.priority"></div>
          <div class="alert-content">
            <div class="alert-header">
              <div class="alert-badges">
                <span class="alert-type-badge" :class="alert.alert_type">
                  <i :class="getAlertTypeIcon(alert.alert_type)"></i>
                  {{ alert.alert_type_display }}
                </span>
                <span class="alert-priority-badge" :class="alert.priority">
                  {{ alert.priority_display }}
                </span>
                <span class="alert-state">{{ alert.policy_event_state }}</span>
              </div>
              <span class="alert-time">{{ formatTime(alert.created_at) }}</span>
            </div>
            
            <h3 class="alert-title">{{ alert.title }}</h3>
            
            <div class="alert-footer">
              <div class="alert-actions">
                <button 
                  v-if="!alert.is_read"
                  @click="markRead(alert)" 
                  class="action-btn"
                  title="Mark as read"
                >
                  <i class="fas fa-check"></i>
                  Mark Read
                </button>
                <button 
                  @click="dismissAlert(alert)" 
                  class="action-btn dismiss"
                  title="Dismiss"
                >
                  <i class="fas fa-times"></i>
                  Dismiss
                </button>
                <button 
                  @click="viewDetails(alert)" 
                  class="action-btn view"
                  title="View details"
                >
                  <i class="fas fa-eye"></i>
                  Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alert Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay" @click.self="showDetailsModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            <i :class="getAlertTypeIcon(selectedAlert.alert_type)" class="mr-2"></i>
            Alert Details
          </h2>
          <button @click="showDetailsModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="selectedAlert">
          <div class="detail-section">
            <label>Alert Type</label>
            <span class="alert-type-badge" :class="selectedAlert.alert_type">
              {{ selectedAlert.alert_type_display }}
            </span>
          </div>
          
          <div class="detail-section">
            <label>Priority</label>
            <span class="alert-priority-badge" :class="selectedAlert.priority">
              {{ selectedAlert.priority_display }}
            </span>
          </div>
          
          <div class="detail-section">
            <label>Title</label>
            <p class="detail-text">{{ selectedAlert.title }}</p>
          </div>
          
          <div class="detail-section">
            <label>Message</label>
            <p class="detail-text">{{ selectedAlert.message }}</p>
          </div>
          
          <div class="detail-section" v-if="selectedAlert.policy_event_title">
            <label>Related Policy Event</label>
            <div class="policy-event-card">
              <span class="event-state">{{ selectedAlert.policy_event_state }}</span>
              <h4>{{ selectedAlert.policy_event_title }}</h4>
              <span v-if="selectedAlert.policy_event_effective_date" class="event-date">
                <i class="fas fa-calendar-alt"></i>
                Effective: {{ formatDate(selectedAlert.policy_event_effective_date) }}
              </span>
            </div>
          </div>
          
          <div class="detail-section" v-if="selectedAlert.triggered_by_view_name">
            <label>Triggered By</label>
            <p class="detail-text">
              <i class="fas fa-bookmark mr-2"></i>
              {{ selectedAlert.triggered_by_view_name }}
            </p>
          </div>
          
          <div class="detail-section">
            <label>Created</label>
            <p class="detail-text">{{ formatDateTime(selectedAlert.created_at) }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            v-if="!selectedAlert.is_read"
            @click="markRead(selectedAlert); showDetailsModal = false" 
            class="btn btn-primary"
          >
            <i class="fas fa-check mr-2"></i>
            Mark as Read
          </button>
          <button @click="showDetailsModal = false" class="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'RegulatoryAlerts',
  
  data() {
    return {
      loading: true,
      alerts: [],
      activeFilter: 'all',
      showDetailsModal: false,
      selectedAlert: null,
      markingAllRead: false
    };
  },
  
  computed: {
    filteredAlerts() {
      let filtered = this.alerts;
      
      switch (this.activeFilter) {
        case 'unread':
          filtered = this.alerts.filter(a => !a.is_read);
          break;
        case 'effective_soon':
          filtered = this.alerts.filter(a => a.alert_type === 'effective_soon');
          break;
        case 'high_impact':
          filtered = this.alerts.filter(a => a.alert_type === 'high_impact');
          break;
      }
      
      return filtered;
    },
    
    unreadCount() {
      return this.alerts.filter(a => !a.is_read).length;
    },
    
    urgentCount() {
      return this.alerts.filter(a => a.priority === 'urgent' && !a.is_read).length;
    }
  },
  
  mounted() {
    this.loadAlerts();
  },
  
  methods: {
    async loadAlerts() {
      this.loading = true;
      try {
        const response = await api.get('/regulatory/alerts/');
        this.alerts = response.data.results || response.data;
      } catch (error) {
        console.error('Failed to load alerts:', error);
        this.$toast?.error?.('Failed to load alerts');
      } finally {
        this.loading = false;
      }
    },
    
    async markRead(alert) {
      try {
        await api.post(`/regulatory/alerts/${alert.id}/mark_read/`);
        alert.is_read = true;
      } catch (error) {
        console.error('Failed to mark alert as read:', error);
      }
    },
    
    async dismissAlert(alert) {
      try {
        await api.post(`/regulatory/alerts/${alert.id}/dismiss/`);
        this.alerts = this.alerts.filter(a => a.id !== alert.id);
      } catch (error) {
        console.error('Failed to dismiss alert:', error);
      }
    },
    
    async markAllRead() {
      this.markingAllRead = true;
      try {
        await api.post('/regulatory/alerts/mark_all_read/');
        this.alerts.forEach(a => a.is_read = true);
        this.$toast?.success?.('All alerts marked as read');
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        this.$toast?.error?.('Failed to mark all as read');
      } finally {
        this.markingAllRead = false;
      }
    },
    
    viewDetails(alert) {
      this.selectedAlert = alert;
      this.showDetailsModal = true;
      
      // Auto-mark as read when viewing details
      if (!alert.is_read) {
        this.markRead(alert);
      }
    },
    
    getAlertTypeIcon(type) {
      const icons = {
        'effective_soon': 'fas fa-calendar-alt',
        'high_impact': 'fas fa-exclamation-triangle',
        'new_rule': 'fas fa-plus-circle',
        'expiring': 'fas fa-clock',
        'status_change': 'fas fa-exchange-alt'
      };
      return icons[type] || 'fas fa-bell';
    },
    
    formatTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.regulatory-alerts-page {
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;
  background: #f8fafc;
  min-height: 100vh;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.accessiq-logo-img {
  height: 40px;
  width: auto;
}

.brand-subtitle {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
}

.page-description {
  color: #64748b;
  margin: 0;
  max-width: 400px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Stats Bar */
.stats-bar {
  display: flex;
  gap: 24px;
  padding: 16px 20px;
  background: white;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
}

/* Filter Tabs */
.filter-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.filter-tab {
  padding: 10px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.filter-tab:hover {
  border-color: #0d9488;
  color: #0d9488;
}

.filter-tab.active {
  background: #0d9488;
  border-color: #0d9488;
  color: white;
}

.tab-badge {
  background: #ef4444;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  margin-left: 6px;
}

.filter-tab.active .tab-badge {
  background: white;
  color: #0d9488;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-spinner {
  text-align: center;
  color: #64748b;
}

.loading-spinner i {
  color: #0d9488;
  margin-bottom: 16px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.empty-state i {
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: #334155;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #64748b;
  margin: 0;
}

/* Alerts List */
.alerts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Alert Card */
.alert-card {
  display: flex;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.2s;
}

.alert-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.alert-card.unread {
  border-left: none;
  background: #f0fdfa;
}

.alert-card.urgent {
  border-color: #fecaca;
}

.alert-card.high {
  border-color: #fed7aa;
}

.alert-priority-bar {
  width: 4px;
  flex-shrink: 0;
}

.alert-priority-bar.urgent { background: #ef4444; }
.alert-priority-bar.high { background: #f59e0b; }
.alert-priority-bar.normal { background: #6b7280; }
.alert-priority-bar.low { background: #94a3b8; }

.alert-content {
  flex: 1;
  padding: 16px;
}

.alert-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.alert-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.alert-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.alert-type-badge.effective_soon {
  background: #fef3c7;
  color: #d97706;
}

.alert-type-badge.high_impact {
  background: #fef2f2;
  color: #dc2626;
}

.alert-type-badge.new_rule {
  background: #d1fae5;
  color: #059669;
}

.alert-type-badge.expiring {
  background: #dbeafe;
  color: #2563eb;
}

.alert-type-badge.status_change {
  background: #f3e8ff;
  color: #9333ea;
}

.alert-priority-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.alert-priority-badge.urgent {
  background: #ef4444;
  color: white;
}

.alert-priority-badge.high {
  background: #f59e0b;
  color: white;
}

.alert-priority-badge.normal {
  background: #e2e8f0;
  color: #64748b;
}

.alert-priority-badge.low {
  background: #f1f5f9;
  color: #94a3b8;
}

.alert-state {
  background: #0d9488;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.alert-time {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}

.alert-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.alert-footer {
  display: flex;
  justify-content: flex-end;
}

.alert-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.action-btn.dismiss:hover {
  background: #fef2f2;
  color: #ef4444;
}

.action-btn.view:hover {
  background: #ccfbf1;
  color: #0d9488;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 550px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 18px;
  margin: 0;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.detail-section {
  margin-bottom: 16px;
}

.detail-section label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.detail-text {
  font-size: 14px;
  color: #0f172a;
  margin: 0;
  line-height: 1.5;
}

.policy-event-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  border-left: 3px solid #0d9488;
}

.policy-event-card .event-state {
  display: inline-block;
  background: #0d9488;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
}

.policy-event-card h4 {
  font-size: 14px;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.policy-event-card .event-date {
  font-size: 12px;
  color: #64748b;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: #0d9488;
  color: white;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-outline-primary {
  background: white;
  color: #0d9488;
  border: 1px solid #0d9488;
}

.btn-outline-primary:hover {
  background: #f0fdfa;
}

.btn-outline-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }
  
  .stats-bar {
    justify-content: space-around;
  }
  
  .filter-tabs {
    justify-content: flex-start;
  }
  
  .alert-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .alert-time {
    align-self: flex-start;
  }
}
</style>
